import {
  AttachFile,
  AutoAwesome,
  BusinessCenter,
  Close,
  ExpandMore,
  Info,
  Settings,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import KnowledgeBaseSelect from "../../components/KnowledgeBaseSelect";
import LoadingButton from "../../components/LoadingButton";
import { Company } from "../../types";
import { hostname, parseError } from "../../utils";

interface ContentStrategyGenerateProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  company: Company;
  getToken: Function;
  eventListener: Function;
  onComplete: Function;
}

export default ({
  open,
  onClose,
  companyId,
  company,
  getToken,
  eventListener,
  onComplete,
}: ContentStrategyGenerateProps) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [fetchingICPs, setFetchingICPs] = useState(false);
  const [idealCustomerProfiles, setIdealCustomerProfiles] = useState<any[]>([]);
  const [formData, setFormData] = useState<any>({
    name: "",
    industryFocus: "",
    targetAudience: "",
    useCompanyInfo: true,
    useKnowledgeBase: false,
    knowledgeBasePath: "/",
    idealCustomerProfileId: "none",
  });
  const [expandedPanel, setExpandedPanel] = useState<string | false>(
    "companyPanel"
  );

  // Fetch ideal customer profiles
  const fetchIdealCustomerProfiles = async () => {
    if (!companyId) return;

    setFetchingICPs(true);
    try {
      const token = await getToken();
      const response = await axios.get(`${hostname}/content/profiles`, {
        params: { companyId, pageSize: 100 }, // Get all ICPs
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setIdealCustomerProfiles(response.data.items || []);
    } catch (error) {
      console.error("Error fetching ideal customer profiles:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setFetchingICPs(false);
    }
  };

  const handleExpandPanel =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? panel : false);
    };

  // Reset form and fetch ICPs when dialog opens
  useEffect(() => {
    if (open) {
      // Initialize form with company data if useCompanyInfo is true
      const initialData = {
        name: `${
          company?.name || "Company"
        } Content Strategy - ${new Date().toLocaleDateString()}`,
        industryFocus: "",
        targetAudience: "",
        useCompanyInfo: true,
        useKnowledgeBase: false,
        knowledgeBasePath: "/",
        idealCustomerProfileId: "none",
      };

      // If useCompanyInfo is true and company data exists, populate fields
      if (initialData.useCompanyInfo && company) {
        initialData.industryFocus = company.industry || "";
        initialData.targetAudience = company.description || "";
      }

      setFormData(initialData);

      // Fetch ideal customer profiles
      fetchIdealCustomerProfiles();
    }
  }, [open, company, companyId]);

  const handleInputChange = (event: any) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormData({ ...formData, [name]: checked });

    // If toggling useCompanyInfo, populate or clear the company fields
    if (name === "useCompanyInfo") {
      if (checked && company) {
        // Populate fields with current company data
        setFormData({
          ...formData,
          useCompanyInfo: checked,
          industryFocus: company.industry || "",
          targetAudience: company.description || "",
        });
      } else if (!checked) {
        // Clear company-related fields
        setFormData({
          ...formData,
          useCompanyInfo: checked,
          industryFocus: "",
          targetAudience: "",
        });
      }
    }
  };

  const generateStrategy = async () => {
    if (!formData.name) {
      eventListener({
        type: "SET_ERROR",
        error: "Strategy name is required",
      });
      return;
    }

    setGenerating(true);

    try {
      const token = await getToken();

      // Format input data for the generation endpoint
      const inputData = {
        name: formData.name,
        industryFocus: formData.industryFocus,
        targetAudience: formData.targetAudience,
        useCompanyInfo: formData.useCompanyInfo,
        useKnowledgeBase: formData.useKnowledgeBase,
        knowledgeBasePath: formData.knowledgeBasePath,
        idealCustomerProfileId:
          formData.idealCustomerProfileId !== "none"
            ? formData.idealCustomerProfileId
            : null, // Include ICP if selected
      };

      await axios.post(
        `${hostname}/content/strategy/generate`,
        {
          companyId,
          data: inputData,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Show success message indicating background generation started
      eventListener({
        type: "SET_SUCCESS",
        info: "Content strategy generation started! This may take a few minutes. Check back shortly to see your completed strategy.",
      });

      // Call onComplete to refresh the list
      onComplete();
    } catch (error) {
      console.error("Error generating content strategy:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setGenerating(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AutoAwesome color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">
              Generate Content Strategy with AI
            </Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>How This Works</AlertTitle>
          Our AI will analyze your company information and knowledge base
          documents to create a comprehensive content strategy with pillars
          aligned to your sales funnel stages (TOFU, MOFU, BOFU).
        </Alert>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <TextField
          name="name"
          label="Strategy Name"
          value={formData.name}
          onChange={handleInputChange}
          fullWidth
          required
          margin="normal"
          error={!formData.name}
          helperText={!formData.name ? "Strategy name is required" : ""}
        />

        <Accordion
          expanded={expandedPanel === "companyPanel"}
          onChange={handleExpandPanel("companyPanel")}
          sx={{ mt: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <BusinessCenter sx={{ mr: 1 }} />
            <Typography>Company & Target Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.useCompanyInfo}
                  onChange={handleSwitchChange}
                  name="useCompanyInfo"
                />
              }
              label="Use current company information as a starting point"
            />

            <Box sx={{ mt: 2, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="industryFocus"
                    value={formData.industryFocus}
                    label="Industry Focus"
                    onChange={handleInputChange}
                    placeholder="e.g., SaaS, Healthcare, Finance"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="targetAudience"
                    label="Target Audience"
                    value={formData.targetAudience}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                    fullWidth
                    placeholder="Describe your target audience and their key challenges"
                  />
                </Grid>

                {/* Ideal Customer Profile Selection */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Ideal Customer Profile</InputLabel>
                    <Select
                      value={formData.idealCustomerProfileId}
                      label="Ideal Customer Profile"
                      name="idealCustomerProfileId"
                      onChange={handleInputChange}
                      disabled={fetchingICPs}
                    >
                      <MenuItem value="none">
                        <em>------</em>
                      </MenuItem>
                      {idealCustomerProfiles.map((profile) => (
                        <MenuItem key={profile.id} value={profile.id}>
                          {profile.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {fetchingICPs && (
                      <Box sx={{ width: "100%", mt: 1 }}>
                        <LinearProgress />
                      </Box>
                    )}
                  </FormControl>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Select an Ideal Customer Profile to align your content
                    strategy with your ICP
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedPanel === "knowledgePanel"}
          onChange={handleExpandPanel("knowledgePanel")}
          sx={{ mt: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <AttachFile sx={{ mr: 1 }} />
            <Typography>Knowledge Base Documents</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.useKnowledgeBase}
                  onChange={handleSwitchChange}
                  name="useKnowledgeBase"
                />
              }
              label="Include knowledge base documents"
            />

            {formData.useKnowledgeBase && (
              <Box sx={{ mt: 2 }}>
                <KnowledgeBaseSelect
                  getToken={getToken}
                  eventListener={eventListener}
                  company={company}
                  value={formData.knowledgeBasePath}
                  onValueChange={(v) =>
                    setFormData({ ...formData, knowledgeBasePath: v })
                  }
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedPanel === "advancedPanel"}
          onChange={handleExpandPanel("advancedPanel")}
          sx={{ mt: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Settings sx={{ mr: 1 }} />
            <Typography>Advanced Options</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="info" icon={<Info />}>
              <Typography variant="body2">
                The AI will generate a comprehensive content strategy including:
              </Typography>
              <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                <li>Sales narrative that aligns with your business goals</li>
                <li>Detailed target audience description</li>
                <li>Multiple content pillars organized by funnel stage</li>
                <li>Topic suggestions for each content pillar</li>
                <li>Strategic recommendations for content development</li>
              </Box>
              {formData.idealCustomerProfileId !== "none" && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  <strong>Ideal Customer Profile Integration:</strong> Your
                  strategy will be aligned with the selected ICP, incorporating
                  its business profile, challenges, and personas to create
                  content that resonates with your ideal customers.
                </Typography>
              )}
            </Alert>
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={generateStrategy}
          loading={generating}
          disabled={generating || !formData.name}
          startIcon={<AutoAwesome />}
        >
          {generating ? "Generating..." : "Generate Strategy"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
